import type { UiTestFeatureResult, UiTestScenarioSummary } from '@/modules/ui-test/uiTest.types'
import type { useUiTestRun } from '@/modules/ui-test/UiTestRunContext'

export type UiTestRunner = Pick<
  ReturnType<typeof useUiTestRun>,
  'appendLog' | 'setProgress' | 'notifyDataChanged' | 'screenRootRef' | 'getLogs' | 'navigate'
>

export type UiTestFeatureDef = {
  id: string
  label: string
  run: (uiTest: UiTestRunner) => Promise<{ status: 'ok' | 'skip' | 'fail'; message: string; evaluation?: string }>
}

export async function runUiTestSuite(
  uiTest: UiTestRunner,
  features: UiTestFeatureDef[],
  suiteLabel: string,
): Promise<UiTestScenarioSummary> {
  const featureResults: UiTestFeatureResult[] = []
  let passed = 0
  let skipped = 0
  let failed = 0
  const total = features.length

  uiTest.setProgress({ done: 0, total, message: `Bắt đầu: ${suiteLabel}` })

  for (let i = 0; i < features.length; i += 1) {
    const feature = features[i]
    uiTest.setProgress({ done: i, total, message: feature.label })
    uiTest.appendLog({
      id: `${feature.id}-start`,
      label: feature.label,
      status: 'running',
      message: `Bắt đầu kiểm tra: ${feature.label}`,
    })

    try {
      const result = await feature.run(uiTest)
      featureResults.push({
        id: feature.id,
        label: feature.label,
        status: result.status,
        message: result.message,
        evaluation: result.evaluation,
      })
      if (result.status === 'ok') passed += 1
      else if (result.status === 'skip') skipped += 1
      else failed += 1

      uiTest.appendLog({
        id: `${feature.id}-end`,
        label: feature.label,
        status: result.status,
        message: result.message,
      })
    } catch (e) {
      failed += 1
      const message = (e as { message?: string })?.message || 'Lỗi không xác định'
      featureResults.push({ id: feature.id, label: feature.label, status: 'fail', message })
      uiTest.appendLog({
        id: `${feature.id}-err`,
        label: feature.label,
        status: 'fail',
        message,
      })
    }

    uiTest.setProgress({
      done: i + 1,
      total,
      message: `${feature.label} — ${i + 1}/${total}`,
    })
  }

  const summary: UiTestScenarioSummary = {
    ok: failed === 0,
    passed,
    skipped,
    failed,
    total,
    features: featureResults,
    message: `${suiteLabel}: đạt ${passed}, bỏ qua ${skipped}, lỗi ${failed} / ${total} tính năng`,
  }

  uiTest.setProgress({ done: total, total, message: summary.message })
  return summary
}
